import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { SiteHeader, SiteFooter } from './Layout';
import TrendChart from './TrendChart';
import {
  normalizeType, hasVal, fmtWon, fmtAum, fmtPct, pctColor, disparity,
  RETURN_PERIODS, getTrendValues, COMPARE_COLORS, glassCard,
} from './etfUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_COMPARE = 4;

function Shell({ children }) {
  return (
    <div className="min-h-screen text-white font-sans" style={{ background: '#060c1a' }}>
      <SiteHeader />
      <div className="relative max-w-6xl mx-auto px-4 md:px-8 pt-24 pb-16" style={{ zIndex: 10 }}>
        {children}
      </div>
      <SiteFooter />
    </div>
  );
}

// 비교 표의 한 지표 행: 라벨 + 각 ETF 셀 + 최고값 강조
function MetricRow({ label, etfs, get, fmt, color, highlight }) {
  const nums = etfs.map((e) => get(e));
  // highlight: 'max' | 'min' | null — 가장 유리한 값에 강조
  let best = null;
  const valid = nums.filter((v) => hasVal(v)).map(Number);
  if (highlight && valid.length > 1) {
    best = highlight === 'max' ? Math.max(...valid) : Math.min(...valid);
  }
  return (
    <tr className="border-b border-white/8">
      <th className="text-left text-sm text-white/50 font-medium px-4 py-3 sticky left-0 bg-[#0a1124] z-10">{label}</th>
      {nums.map((v, i) => {
        const isBest = best !== null && hasVal(v) && Number(v) === best;
        return (
          <td key={i} className={`px-4 py-3 text-sm font-semibold tabular-nums text-center whitespace-nowrap ${isBest ? 'bg-blue-500/10' : ''}`}
            style={color ? { color: color(v) } : { color: '#fff' }}>
            {fmt(v)}{isBest && <span className="ml-1 text-[10px] text-blue-300">▲</span>}
          </td>
        );
      })}
    </tr>
  );
}

export default function EtfCompare() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('korea');

  // 선택된 id 목록은 URL(?ids=)로 관리 → 공유·뒤로가기 가능
  const selectedIds = useMemo(() => {
    const raw = params.get('ids') || '';
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [params]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/products`)
      .then((r) => r.json())
      .then((data) => setProducts((Array.isArray(data) ? data : []).filter((p) => !['isa', 'pension', 'irp'].includes(p.id))))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setIds = (ids) => {
    const next = new URLSearchParams(params);
    if (ids.length) next.set('ids', ids.join(','));
    else next.delete('ids');
    setParams(next, { replace: true });
  };

  const toggle = (id) => {
    if (selectedIds.includes(id)) setIds(selectedIds.filter((x) => x !== id));
    else if (selectedIds.length < MAX_COMPARE) setIds([...selectedIds, id]);
  };

  const selected = selectedIds
    .map((id) => products.find((p) => String(p.id) === String(id)))
    .filter(Boolean);

  const listed = products.filter((p) => normalizeType(p.type) === tab);

  if (loading) return <Shell><p className="text-center text-white/50 py-20">불러오는 중...</p></Shell>;

  return (
    <Shell>
      <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 text-sm mb-6">
        <ArrowLeft className="w-4 h-4" /> 목록으로
      </Link>

      <h1 className="text-3xl font-black mb-1">ETF 비교</h1>
      <p className="text-white/50 text-sm mb-8">최대 {MAX_COMPARE}개까지 선택해 시세·수익률·추이를 나란히 비교하세요.</p>

      {/* 선택 패널 */}
      <div className="rounded-3xl p-6 mb-6" style={glassCard}>
        <div className="flex items-center gap-2 mb-5 p-2 rounded-full w-fit" style={{ background: 'rgba(0,0,0,0.2)' }}>
          {[['korea', '🇰🇷 한국형'], ['usa', '🇺🇸 미국형']].map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)}
              className={`font-bold px-5 py-2 rounded-full text-sm transition-all ${tab === t ? 'text-white shadow-md' : 'text-gray-400 hover:bg-white/10'}`}
              style={tab === t ? { background: 'rgba(59, 130, 246, 0.8)' } : {}}>
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {listed.length === 0 && <p className="text-white/40 text-sm py-2">등록된 ETF가 없습니다.</p>}
          {listed.map((p) => {
            const on = selectedIds.includes(String(p.id));
            const full = !on && selectedIds.length >= MAX_COMPARE;
            return (
              <button key={p.id} onClick={() => toggle(String(p.id))} disabled={full}
                className={`px-3.5 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                  on ? 'bg-blue-600 border-blue-500 text-white'
                     : full ? 'border-white/10 text-white/25 cursor-not-allowed'
                            : 'border-white/15 text-white/70 hover:bg-white/10'}`}>
                {p.name || p.id}
              </button>
            );
          })}
        </div>
      </div>

      {selected.length < 2 ? (
        <div className="text-center text-white/40 py-16 bg-white/5 rounded-2xl border border-white/10">
          비교할 ETF를 2개 이상 선택하세요.
        </div>
      ) : (
        <>
          {/* 추이 오버레이 차트 (지수화: 시작 100 기준) */}
          <div className="rounded-3xl p-6 mb-6" style={glassCard}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold">수익률 추이 비교</h2>
              <span className="text-[11px] text-white/35">1년 전 = 100 기준 지수화</span>
            </div>
            <p className="text-xs text-white/35 mb-4">기간 수익률로 역산한 추정치입니다.</p>
            <TrendChart normalize
              series={selected.map((e, i) => ({
                name: e.name, color: COMPARE_COLORS[i % COMPARE_COLORS.length], values: getTrendValues(e),
              }))} />
            <div className="flex flex-wrap gap-4 mt-4">
              {selected.map((e, i) => (
                <div key={e.id} className="flex items-center gap-2 text-sm text-white/70">
                  <span className="w-3 h-3 rounded-full" style={{ background: COMPARE_COLORS[i % COMPARE_COLORS.length] }} />
                  {e.name}
                </div>
              ))}
            </div>
          </div>

          {/* 비교 표 */}
          <div className="rounded-2xl overflow-x-auto" style={glassCard}>
            <table className="w-full border-collapse min-w-[480px]">
              <thead>
                <tr className="border-b border-white/12">
                  <th className="text-left text-xs text-white/40 font-semibold px-4 py-4 sticky left-0 bg-[#0a1124] z-10">항목</th>
                  {selected.map((e, i) => (
                    <th key={e.id} className="px-4 py-4 text-center min-w-[140px]">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COMPARE_COLORS[i % COMPARE_COLORS.length] }} />
                        <Link to={`/etf/${encodeURIComponent(e.id)}`} className="font-bold text-sm text-white hover:text-blue-300 no-underline">
                          {e.name}
                        </Link>
                        <button onClick={() => toggle(String(e.id))} title="제외"
                          className="p-0.5 rounded text-white/30 hover:text-red-300 hover:bg-red-500/10">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {hasVal(e.ticker) && <div className="text-xs text-white/40 font-mono mt-0.5">{e.ticker}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <MetricRow label="구분" etfs={selected} get={(e) => normalizeType(e.type)} fmt={(v) => (v === 'usa' ? '미국형' : '한국형')} />
                <MetricRow label="월배당" etfs={selected} get={(e) => e.isMonthly} fmt={(v) => (v ? '○' : '—')} />
                <MetricRow label="현재가" etfs={selected} get={(e) => e.price} fmt={fmtWon} />
                <MetricRow label="기준가 (NAV)" etfs={selected} get={(e) => e.nav} fmt={fmtWon} />
                <MetricRow label="괴리율" etfs={selected} get={(e) => disparity(e.price, e.nav)} fmt={fmtPct} color={pctColor} />
                <MetricRow label="순자산 (AUM)" etfs={selected} get={(e) => e.aum} fmt={fmtAum} color={() => '#fff'} highlight="max" />
                {RETURN_PERIODS.map(([k, label]) => (
                  <MetricRow key={k} label={`수익률 ${label}`} etfs={selected}
                    get={(e) => (e.returns || {})[k]} fmt={fmtPct} color={pctColor} highlight="max" />
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-white/30 mt-3">▲ 표시는 해당 항목에서 가장 우수한 값입니다.</p>
        </>
      )}
    </Shell>
  );
}
